function createTimestamp(daysAgo, hour = 9, minute = 0) {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  date.setHours(hour, minute, 0, 0);
  return date.toISOString();
}

function createProduct({
  id,
  sku,
  name,
  slug,
  categoryId,
  categoryName,
  brandName,
  image,
  price,
  oldPrice,
  stock,
  unit,
  description,
  tags,
  isFeatured = false,
  isBestSeller = false,
  rating = 4.4,
  reviewCount = 0,
  updatedAt,
}) {
  const inStock = stock > 0;

  return {
    id,
    sku,
    slug,
    name,
    description,
    categoryId,
    categoryName,
    brandId: "",
    brandName,
    image,
    gallery: image ? [image] : [],
    price,
    oldPrice,
    priceMax: undefined,
    unit,
    sale: Boolean(oldPrice && oldPrice > price),
    quickViewOnly: !inStock,
    tags,
    discountPercentage:
      oldPrice && oldPrice > price
        ? Math.round(((oldPrice - price) / oldPrice) * 100)
        : 0,
    rating,
    reviewCount,
    stock,
    stockStatus: inStock ? "IN_STOCK" : "OUT_OF_STOCK",
    inStock,
    isFeatured,
    isBestSeller,
    variant: {
      id: `${id}-default`,
      value: unit.split(" ")[0] || "",
      unit: unit.split(" ").slice(1).join(" "),
      price,
      originalPrice: oldPrice,
      stock,
    },
    updatedAt,
  };
}

export function createDefaultSettings() {
  return {
    storeName: "GrozyKart Admin Workspace",
    supportEmail: "ops@grozykart.in",
    supportPhone: "+91 98765 43210",
    orderPrefix: "GK",
    deliveryFee: 29,
    taxRate: 5,
    lowStockThreshold: 12,
    announcement:
      "Catalog edits are stored locally in this Parcel admin until write APIs are connected.",
  };
}

export function createSeedCustomers() {
  return [
    {
      id: "customer-1",
      name: "Aarav Menon",
      email: "aarav.menon@example.com",
      phone: "+91 98760 11011",
      city: "Bengaluru",
      status: "VIP",
      segment: "Weekly household",
      notes: "Prefers early morning delivery windows.",
    },
    {
      id: "customer-2",
      name: "Nisha Iyer",
      email: "nisha.iyer@example.com",
      phone: "+91 98760 22022",
      city: "Chennai",
      status: "Active",
      segment: "Fresh produce",
      notes: "Frequently orders fruits and dairy.",
    },
    {
      id: "customer-3",
      name: "Rohan Gupta",
      email: "rohan.gupta@example.com",
      phone: "+91 98760 33033",
      city: "Hyderabad",
      status: "Watch",
      segment: "Offer driven",
      notes: "Only converts on deal-heavy baskets.",
    },
    {
      id: "customer-4",
      name: "Meera Patel",
      email: "meera.patel@example.com",
      phone: "+91 98760 44044",
      city: "Mumbai",
      status: "Active",
      segment: "Breakfast staples",
      notes: "High repeat frequency on milk and bread.",
    },
    {
      id: "customer-5",
      name: "Kabir Shah",
      email: "kabir.shah@example.com",
      phone: "+91 98760 55055",
      city: "Pune",
      status: "Active",
      segment: "Snacks",
      notes: "Responds well to add-on recommendations.",
    },
  ];
}

export function createSeedOrders(products, customers, orderPrefix = "GK") {
  const productMap = new Map(products.map((product) => [product.id, product]));

  const createOrderItem = (productId, quantity) => {
    const product = productMap.get(productId);

    if (!product) {
      return null;
    }

    return {
      productId: product.id,
      name: product.name,
      quantity,
      unit: product.unit,
      price: product.price,
      lineTotal: product.price * quantity,
    };
  };

  const orderDefinitions = [
    {
      id: `${orderPrefix}-1048`,
      customerId: "customer-1",
      fulfillmentStatus: "Packed",
      paymentStatus: "Paid",
      paymentMethod: "Razorpay",
      deliveryWindow: "Today, 4:00 PM - 7:00 PM",
      address: "12 Residency Road, Bengaluru 560001",
      note: "Call before arriving at the gate.",
      placedAt: createTimestamp(0, 11, 25),
      items: [
        createOrderItem(products[0]?.id, 2),
        createOrderItem(products[4]?.id, 1),
        createOrderItem(products[6]?.id, 1),
      ].filter(Boolean),
    },
    {
      id: `${orderPrefix}-1047`,
      customerId: "customer-2",
      fulfillmentStatus: "Out for Delivery",
      paymentStatus: "Paid",
      paymentMethod: "UPI",
      deliveryWindow: "Today, 1:00 PM - 4:00 PM",
      address: "8 Sterling Avenue, Chennai 600028",
      note: "Leave with security if unreachable.",
      placedAt: createTimestamp(0, 9, 10),
      items: [
        createOrderItem(products[2]?.id, 1),
        createOrderItem(products[3]?.id, 2),
      ].filter(Boolean),
    },
    {
      id: `${orderPrefix}-1046`,
      customerId: "customer-4",
      fulfillmentStatus: "Delivered",
      paymentStatus: "Paid",
      paymentMethod: "Razorpay",
      deliveryWindow: "Yesterday, 7:00 AM - 10:00 AM",
      address: "44 Palm Street, Mumbai 400053",
      note: "Repeat staple basket.",
      placedAt: createTimestamp(1, 7, 45),
      items: [
        createOrderItem(products[4]?.id, 2),
        createOrderItem(products[5]?.id, 2),
        createOrderItem(products[7]?.id, 1),
      ].filter(Boolean),
    },
    {
      id: `${orderPrefix}-1045`,
      customerId: "customer-3",
      fulfillmentStatus: "Pending",
      paymentStatus: "Pending",
      paymentMethod: "Cash on Delivery",
      deliveryWindow: "Tomorrow, 7:00 AM - 10:00 AM",
      address: "19 Green Meadows, Hyderabad 500081",
      note: "Hold if tomatoes go out of stock.",
      placedAt: createTimestamp(1, 18, 5),
      items: [
        createOrderItem(products[1]?.id, 2),
        createOrderItem(products[8]?.id, 1),
      ].filter(Boolean),
    },
    {
      id: `${orderPrefix}-1044`,
      customerId: "customer-5",
      fulfillmentStatus: "Cancelled",
      paymentStatus: "Refunded",
      paymentMethod: "Razorpay",
      deliveryWindow: "Yesterday, 4:00 PM - 7:00 PM",
      address: "5 Clover Park, Pune 411045",
      note: "Customer requested cancellation before dispatch.",
      placedAt: createTimestamp(2, 16, 20),
      items: [
        createOrderItem(products[6]?.id, 3),
        createOrderItem(products[3]?.id, 1),
      ].filter(Boolean),
    },
  ];

  return orderDefinitions.map((order) => {
    const customer = customers.find((item) => item.id === order.customerId);
    const total = order.items.reduce((sum, item) => sum + item.lineTotal, 0);

    return {
      ...order,
      customerName: customer?.name || "Guest Customer",
      customerEmail: customer?.email || "",
      customerPhone: customer?.phone || "",
      total,
    };
  });
}

export function buildMockAdminSnapshot() {
  const categories = [
    {
      id: "fresh-vegetables",
      name: "Fresh Vegetables",
      slug: "fresh-vegetables",
      icon: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=80&q=80",
      thumbnail:
        "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=900&q=80",
      updatedAt: createTimestamp(2, 10, 0),
    },
    {
      id: "fruits",
      name: "Seasonal Fruits",
      slug: "seasonal-fruits",
      icon: "https://images.unsplash.com/photo-1610832958506-aa56368176cf?auto=format&fit=crop&w=80&q=80",
      thumbnail:
        "https://images.unsplash.com/photo-1610832958506-aa56368176cf?auto=format&fit=crop&w=900&q=80",
      updatedAt: createTimestamp(3, 11, 30),
    },
    {
      id: "dairy-breakfast",
      name: "Dairy & Breakfast",
      slug: "dairy-breakfast",
      icon: "https://images.unsplash.com/photo-1550583724-b2692b85b150?auto=format&fit=crop&w=80&q=80",
      thumbnail:
        "https://images.unsplash.com/photo-1550583724-b2692b85b150?auto=format&fit=crop&w=900&q=80",
      updatedAt: createTimestamp(2, 15, 15),
    },
    {
      id: "staples",
      name: "Staples",
      slug: "staples",
      icon: "https://images.unsplash.com/photo-1586201375761-83865001e31c?auto=format&fit=crop&w=80&q=80",
      thumbnail:
        "https://images.unsplash.com/photo-1586201375761-83865001e31c?auto=format&fit=crop&w=900&q=80",
      updatedAt: createTimestamp(4, 12, 10),
    },
    {
      id: "snacks",
      name: "Snacks",
      slug: "snacks",
      icon: "https://images.unsplash.com/photo-1599490659213-e2b9527bd087?auto=format&fit=crop&w=80&q=80",
      thumbnail:
        "https://images.unsplash.com/photo-1599490659213-e2b9527bd087?auto=format&fit=crop&w=900&q=80",
      updatedAt: createTimestamp(1, 13, 0),
    },
    {
      id: "masalas",
      name: "Masalas",
      slug: "masalas",
      icon: "https://res.cloudinary.com/dlzecb9pn/image/upload/v1777055134/everest-chicken-masala-50g.jpg",
      thumbnail:
        "https://res.cloudinary.com/dlzecb9pn/image/upload/v1777055134/everest-chicken-masala-50g.jpg",
      updatedAt: createTimestamp(1, 10, 0),
    },
    {
      id: "beverages",
      name: "Beverages",
      slug: "beverages",
      icon: "https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?auto=format&fit=crop&w=80&q=80",
      thumbnail:
        "https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?auto=format&fit=crop&w=900&q=80",
      updatedAt: createTimestamp(5, 9, 30),
    },
  ];

  const products = [
    createProduct({
      id: "everest-chicken-masala-50g",
      sku: "EVEREST-CHICKEN-MASALA-50",
      name: "Everest Chicken Masala",
      slug: "everest-chicken-masala-50g",
      categoryId: "masalas",
      categoryName: "Masalas",
      brandName: "Everest",
      image: "https://res.cloudinary.com/dlzecb9pn/image/upload/v1777055134/everest-chicken-masala-50g.jpg",
      price: 46,
      oldPrice: 46,
      stock: 100,
      unit: "50g",
      description: "Everest Chicken Masala mixed masala powder 50 gram box.",
      tags: ["masalas", "spices", "chicken"],
      isFeatured: false,
      isBestSeller: true,
      rating: 4.7,
      reviewCount: 95,
      updatedAt: createTimestamp(0, 10, 0),
    }),
    createProduct({
      id: "everest-meat-masala-50g",
      sku: "EVEREST-MEAT-MASALA-50",
      name: "Everest Meat Masala",
      slug: "everest-meat-masala-50g",
      categoryId: "masalas",
      categoryName: "Masalas",
      brandName: "Everest",
      image: "https://res.cloudinary.com/dlzecb9pn/image/upload/v1777055179/everest-meat-masala-50g.jpg",
      price: 46,
      oldPrice: 46,
      stock: 100,
      unit: "50g",
      description: "Everest Meat Masala mixed masala powder 50 gram box.",
      tags: ["masalas", "spices", "meat"],
      isFeatured: false,
      isBestSeller: false,
      rating: 4.6,
      reviewCount: 78,
      updatedAt: createTimestamp(0, 10, 0),
    }),
    createProduct({
      id: "everest-sabji-masala-50g",
      sku: "EVEREST-SABJI-MASALA-50",
      name: "Everest Sabji Masala",
      slug: "everest-sabji-masala-50g",
      categoryId: "masalas",
      categoryName: "Masalas",
      brandName: "Everest",
      image: "https://res.cloudinary.com/dlzecb9pn/image/upload/v1777055228/everest-sabji-masala-50g.jpg",
      price: 39,
      oldPrice: 39,
      stock: 99,
      unit: "50g",
      description: "Everest Sabji Masala mixed masala powder 50 gram box.",
      tags: ["masalas", "spices", "veg"],
      isFeatured: true,
      isBestSeller: true,
      rating: 4.8,
      reviewCount: 142,
      updatedAt: createTimestamp(0, 10, 0),
    }),
    createProduct({
      id: "everest-sambar-masala-50g",
      sku: "EVEREST-SAMBAR-MASALA-50",
      name: "Everest Sambar Masala",
      slug: "everest-sambar-masala-50g",
      categoryId: "masalas",
      categoryName: "Masalas",
      brandName: "Everest",
      image: "https://res.cloudinary.com/dlzecb9pn/image/upload/v1777056607/EVEREST-SAMBAR-MASALA-50.jpg",
      price: 40,
      oldPrice: 40,
      stock: 100,
      unit: "50g",
      description: "Everest Sambar Masala mixed masala powder 50 gram box.",
      tags: ["masalas", "spices", "sambar"],
      isFeatured: false,
      isBestSeller: false,
      rating: 4.5,
      reviewCount: 61,
      updatedAt: createTimestamp(0, 10, 0),
    }),
    createProduct({
      id: "gk-potato-1kg",
      sku: "GK-POT-001",
      name: "Farm Fresh Potato",
      slug: "farm-fresh-potato",
      categoryId: "fresh-vegetables",
      categoryName: "Fresh Vegetables",
      brandName: "GrozyKart Fresh",
      image:
        "https://images.unsplash.com/photo-1518977676601-b53f82aba655?auto=format&fit=crop&w=900&q=80",
      price: 38,
      oldPrice: 45,
      stock: 28,
      unit: "1 kg",
      description: "Daily-use table potatoes with steady quality and strong repeat demand.",
      tags: ["vegetables", "daily essentials", "fast moving"],
      isFeatured: true,
      isBestSeller: true,
      rating: 4.6,
      reviewCount: 182,
      updatedAt: createTimestamp(0, 8, 30),
    }),
    createProduct({
      id: "gk-tomato-1kg",
      sku: "GK-TOM-014",
      name: "Residency Tomato",
      slug: "residency-tomato",
      categoryId: "fresh-vegetables",
      categoryName: "Fresh Vegetables",
      brandName: "GrozyKart Fresh",
      image:
        "https://images.unsplash.com/photo-1546094096-0df4bcaaa337?auto=format&fit=crop&w=900&q=80",
      price: 26,
      oldPrice: 32,
      stock: 9,
      unit: "1 kg",
      description: "Bright red tomatoes for cooking and salad packs.",
      tags: ["vegetables", "price sensitive", "perishable"],
      isFeatured: true,
      rating: 4.4,
      reviewCount: 144,
      updatedAt: createTimestamp(0, 7, 45),
    }),
    createProduct({
      id: "gk-mango-box",
      sku: "GK-MNG-051",
      name: "Alphonso Mango Select Box",
      slug: "alphonso-mango-select-box",
      categoryId: "fruits",
      categoryName: "Seasonal Fruits",
      brandName: "Ratnagiri Reserve",
      image:
        "https://images.unsplash.com/photo-1553279768-865429fa0078?auto=format&fit=crop&w=900&q=80",
      price: 249,
      oldPrice: 299,
      stock: 14,
      unit: "1 box",
      description: "Seasonal premium mango box used for hero placement in fruit campaigns.",
      tags: ["fruits", "seasonal", "premium"],
      isFeatured: true,
      isBestSeller: true,
      rating: 4.8,
      reviewCount: 89,
      updatedAt: createTimestamp(1, 12, 20),
    }),
    createProduct({
      id: "gk-coconut-each",
      sku: "GK-COC-003",
      name: "Tender Coconut",
      slug: "tender-coconut",
      categoryId: "fruits",
      categoryName: "Seasonal Fruits",
      brandName: "Coastline Farms",
      image:
        "https://images.unsplash.com/photo-1581375074612-d1fd0e661aeb?auto=format&fit=crop&w=900&q=80",
      price: 55,
      oldPrice: undefined,
      stock: 21,
      unit: "1 each",
      description: "Hydrating, high-velocity fruit add-on for summer checkouts.",
      tags: ["fruits", "hydration", "impulse"],
      isBestSeller: true,
      rating: 4.5,
      reviewCount: 64,
      updatedAt: createTimestamp(1, 9, 50),
    }),
    createProduct({
      id: "gk-milk-a2",
      sku: "GK-MLK-021",
      name: "A2 Cow Milk",
      slug: "a2-cow-milk",
      categoryId: "dairy-breakfast",
      categoryName: "Dairy & Breakfast",
      brandName: "Morning Route",
      image:
        "https://images.unsplash.com/photo-1563636619-e9143da7973b?auto=format&fit=crop&w=900&q=80",
      price: 72,
      oldPrice: 79,
      stock: 18,
      unit: "1 litre",
      description: "Premium morning delivery milk pack with stable household retention.",
      tags: ["dairy", "subscription", "high retention"],
      isFeatured: true,
      rating: 4.7,
      reviewCount: 116,
      updatedAt: createTimestamp(0, 6, 15),
    }),
    createProduct({
      id: "gk-bread-brown",
      sku: "GK-BRD-009",
      name: "Brown Bread Classic",
      slug: "brown-bread-classic",
      categoryId: "dairy-breakfast",
      categoryName: "Dairy & Breakfast",
      brandName: "Bake House",
      image:
        "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=900&q=80",
      price: 48,
      oldPrice: undefined,
      stock: 11,
      unit: "1 pack",
      description: "High-repeat breakfast staple with frequent bundle potential.",
      tags: ["breakfast", "bundle", "repeat"],
      rating: 4.3,
      reviewCount: 52,
      updatedAt: createTimestamp(2, 14, 0),
    }),
    createProduct({
      id: "gk-makhana-roasted",
      sku: "GK-SNK-017",
      name: "Roasted Makhana Chili Lime",
      slug: "roasted-makhana-chili-lime",
      categoryId: "snacks",
      categoryName: "Snacks",
      brandName: "Snack Theory",
      image:
        "https://images.unsplash.com/photo-1599490659213-e2b9527bd087?auto=format&fit=crop&w=900&q=80",
      price: 95,
      oldPrice: 119,
      stock: 7,
      unit: "120 g",
      description: "Healthy snack line with sharp margin but low inventory coverage.",
      tags: ["snacks", "healthy", "low stock"],
      isBestSeller: true,
      rating: 4.4,
      reviewCount: 41,
      updatedAt: createTimestamp(0, 10, 5),
    }),
    createProduct({
      id: "gk-rice-basmati",
      sku: "GK-STP-080",
      name: "Aged Basmati Rice",
      slug: "aged-basmati-rice",
      categoryId: "staples",
      categoryName: "Staples",
      brandName: "Harvest Gold",
      image:
        "https://images.unsplash.com/photo-1586201375761-83865001e31c?auto=format&fit=crop&w=900&q=80",
      price: 399,
      oldPrice: 429,
      stock: 41,
      unit: "5 kg",
      description: "High-value pantry staple used in family-sized basket mixes.",
      tags: ["staples", "bulk", "high value"],
      isFeatured: true,
      rating: 4.7,
      reviewCount: 93,
      updatedAt: createTimestamp(3, 16, 30),
    }),
    createProduct({
      id: "gk-coldbrew-coffee",
      sku: "GK-BEV-029",
      name: "Cold Brew Coffee Bottle",
      slug: "cold-brew-coffee-bottle",
      categoryId: "beverages",
      categoryName: "Beverages",
      brandName: "Daily Grind",
      image:
        "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=900&q=80",
      price: 129,
      oldPrice: undefined,
      stock: 0,
      unit: "750 ml",
      description: "Premium impulse beverage that is currently out of stock.",
      tags: ["beverages", "out of stock", "premium"],
      rating: 4.1,
      reviewCount: 26,
      updatedAt: createTimestamp(4, 17, 45),
    }),
  ];

  const settings = createDefaultSettings();
  const customers = createSeedCustomers();
  const orders = createSeedOrders(products, customers, settings.orderPrefix);

  return {
    products,
    categories,
    orders,
    customers,
    settings,
  };
}
